// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { AuthnMethod, type AuthnRequest } from '@imprivata-cloud/authn';
import { ImprHeaders } from '@imprivata-cloud/common';
import type { DomSerialized } from '@imprivata-cloud/components';
import type { AppError } from '@imprivata-cloud/core';
import { base64ToJson, type CodingContext, jsonToBase64 } from '@imprivata-cloud/dataprivacy';
import { ClientHeader, defaultSystemData, Endpoints } from './constants';
import type {
  AuthenticatorVerifyRequest,
  EmailVerificationResponse,
  IdentityVerificationRecordRequest,
  SelfServiceLandingPageDataResponse,
  SupervisedAssuranceCancelRequest,
  SupervisedAssuranceCompleteRequest,
  SupervisedAssuranceStartRequest,
  SupervisedAssuranceStartResponse,
  UserConsentCheckRequest,
  UserConsentCheckResponse,
  UserConsentRevokeRequest,
  UserConsentSubmitRequest,
  UserPasswordChangeRequest,
  VerificationOtpRequest,
  VerificationOtpResponse,
} from './types';
import { createCodingCtxV2, fetcher } from './utils';

const getSelfServiceLandingPageData = async (): Promise<SelfServiceLandingPageDataResponse> => {
  return fetcher(Endpoints.SELF_SERVICE_V2_LANDING_PAGE_GET, { method: 'POST' });
};

const submitConsent = async (data: UserConsentSubmitRequest): Promise<void> => {
  return fetcher(Endpoints.CONSENT_SUBMIT, { method: 'POST', body: JSON.stringify(data) });
};

const revokeConsent = async (data: UserConsentRevokeRequest): Promise<void> => {
  return fetcher(Endpoints.CONSENT_REVOKE, { method: 'POST', body: JSON.stringify(data) });
};

const checkConsent = async (data: UserConsentCheckRequest): Promise<UserConsentCheckResponse> => {
  return fetcher(Endpoints.CONSENT_CHECK, { method: 'POST', body: JSON.stringify(data) });
};

// Get Legal Doc Content from json file
export async function getConsentLegalDocumentContent(url: string): Promise<DomSerialized> {
  return fetcher(url, { method: 'GET', credentials: 'omit' });
}

const enroll = async (authnData: AuthnRequest): Promise<AppError> => {
  const codingContext = await createCodingCtxV2();
  const codingCtxHeader = codingContext.buildImprCodingCtxHeader();

  const factorDataObj = authnData.factorData && base64ToJson(authnData.factorData);
  const systemDataObj = base64ToJson(authnData.systemData);

  return fetcher(Endpoints.SELF_SERVICE_V1_ENROLL, {
    method: 'POST',
    headers: { [ImprHeaders.ImprCodingCtx]: codingCtxHeader },
    body: JSON.stringify({
      ...authnData,
      factorData: factorDataObj && codingContext.encryptJson(factorDataObj),
      systemData: codingContext.encryptJson(systemDataObj),
    }),
  });
};

const changeImprIdpUserPassword = async (
  data: UserPasswordChangeRequest,
  codingContext?: CodingContext,
): Promise<void> => {
  return fetcher(Endpoints.SELF_SERVICE_CHANGE_PASSWORD, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: codingContext ? { 'Impr-Coding-Ctx': codingContext.buildImprCodingCtxHeader() } : {},
  });
};

// w/o tenantId the BE is unhappy and taking it from session is the only way for USS
export async function supervisedAssuranceCancel(
  data: SupervisedAssuranceCancelRequest,
  workflowId: string,
  tenantId?: string,
): Promise<void> {
  return fetcher(
    Endpoints.USER_WEB_SUPERVISED_ASSURANCE_CANCEL,
    {
      method: 'POST',
      body: JSON.stringify(data),
      workflowId,
      headers: { [ImprHeaders.ImprClientName]: jsonToBase64({ name: ClientHeader.IDP_WEB }) },
      keepalive: true,
    },
    tenantId,
  );
}

export async function supervisedAssuranceStart(
  data: SupervisedAssuranceStartRequest,
  workflowId: string,
  tenantId?: string,
): Promise<SupervisedAssuranceStartResponse> {
  return fetcher(
    Endpoints.USER_WEB_SUPERVISED_ASSURANCE_START,
    {
      method: 'POST',
      body: JSON.stringify(data),
      workflowId,
      headers: { [ImprHeaders.ImprClientName]: jsonToBase64({ name: ClientHeader.IDP_WEB }) },
    },
    tenantId,
  );
}

export async function supervisedAssuranceIdentityVerificationRecord(
  data: IdentityVerificationRecordRequest,
  workflowId: string,
  tenantId?: string,
): Promise<void> {
  return fetcher(
    Endpoints.USER_WEB_SUPERVISED_ASSURANCE_IDENTITY_VERIFICATION_RECORD,
    {
      method: 'POST',
      body: JSON.stringify(data),
      workflowId,
      headers: { [ImprHeaders.ImprClientName]: jsonToBase64({ name: ClientHeader.IDP_WEB }) },
    },
    tenantId,
  );
}

export async function supervisedAssuranceAuthenticatorVerify(
  assuranceId: string,
  authenticatorId: string,
  tokenCode: string,
  workflowId: string,
  tenantId?: string,
): Promise<void> {
  const codingContext = await createCodingCtxV2();

  const data: AuthenticatorVerifyRequest = {
    assuranceId: assuranceId || '',
    authenticatorId: authenticatorId || '',
    factorType: AuthnMethod.IMPR_ID,
    factorData: codingContext.encryptJson({ version: '1.0', totp: tokenCode }),
    systemData: codingContext.encryptJson(defaultSystemData),
  };

  return fetcher(
    Endpoints.USER_WEB_SUPERVISED_ASSURANCE_AUTHENTICATOR_VERIFY,
    {
      method: 'POST',
      body: JSON.stringify(data),
      workflowId,
      headers: {
        [ImprHeaders.ImprClientName]: jsonToBase64({ name: ClientHeader.IDP_WEB }),
        [ImprHeaders.ImprCodingCtx]: codingContext.buildImprCodingCtxHeader(),
      },
    },
    tenantId,
  );
}

export async function supervisedAssuranceComplete(
  data: SupervisedAssuranceCompleteRequest,
  workflowId: string,
  tenantId?: string,
): Promise<void> {
  return fetcher(
    Endpoints.USER_WEB_SUPERVISED_ASSURANCE_COMPLETE,
    {
      method: 'POST',
      body: JSON.stringify(data),
      workflowId,
      headers: { [ImprHeaders.ImprClientName]: jsonToBase64({ name: ClientHeader.IDP_WEB }) },
    },
    tenantId,
  );
}

const changePasswordWithEncryption = async (newPassword: string): Promise<void> => {
  const codingContext = await createCodingCtxV2();

  const encryptedPassword = codingContext.encryptString(newPassword);

  return changeImprIdpUserPassword({ newPassword: encryptedPassword }, codingContext);
};

const confirmEmailVerification = async (
  data: VerificationOtpRequest,
  codingCtxHeader: string,
): Promise<VerificationOtpResponse> => {
  return fetcher(Endpoints.SELF_SERVICE_OTP_CONFIRM, {
    method: 'POST',
    headers: { [ImprHeaders.ImprCodingCtx]: codingCtxHeader },
    body: JSON.stringify(data),
  });
};

const requestEmailVerification = async (): Promise<EmailVerificationResponse> => {
  return fetcher(Endpoints.SELF_SERVICE_OTP_REQUEST, { method: 'POST' });
};

export {
  getSelfServiceLandingPageData,
  submitConsent,
  checkConsent,
  enroll,
  changeImprIdpUserPassword,
  revokeConsent,
  changePasswordWithEncryption,
  confirmEmailVerification,
  requestEmailVerification,
};
